import { useCallback, useEffect, useRef, useState } from 'react'
import {
  startStreamingSession,
  postAudioChunk,
  finalizeStream,
  cancelStream,
  pauseStreamingSession,
  resumeStreamingSession,
  RecordingSubmitFailedError,
  toRecordingSubmitFailedError,
} from './streaming/apis/streaming'
import { ChunkSendQueue } from './streaming/chunkSendQueue'
import type {
  Linear16WorkletMessage,
  Linear16WorkletNode,
} from './streaming/types/audio-worklet'
import {
  CancelResult,
  RecordingInterruptedError,
  type InterruptionReason,
  type PausedSessionInfo,
  type RecordingEngineArgs,
  type RecordingEngineReturn,
  type RecordingEngineState,
  type RecordingStartParams,
} from './types'
import { removeYakurekiFromLocalCache } from '@/shared/apis/yakurekiSync'

/**
 * tab hidden (visibilitychange) で即 fallback に倒さず復帰を待つ grace window.
 * back chunk timeout (CHUNK_QUEUE_TIMEOUT_S=30s) + Google abort 観測 ~10s より十分短く、
 * 短時間の tab 切替で黄バナーが出る過剰検知を抑える (= < ~8s が安全).
 */
const VISIBILITY_GRACE_MS = 5_000

/**
 * 録音エンジン (transport 中立の状態機械)。
 *
 * 「録音する」という 1 つのライフサイクル (RecordingEngineState) の上で
 * 2 つの転写 transport を合成する:
 *
 * - streaming transport (既定): AudioContext (sampleRate=16000) + AudioWorklet で
 *   LINEAR16 PCM 100ms chunk を生成し、start / chunks / finalize (multipart audio 同梱)
 *   API に流す (chunks は ChunkSendQueue による直列送信 = in-flight 常に 1 本 +
 *   まとめ送り. backend が到着順で Chirp に feed するため、 順序保証は送信側で担う)。
 *   chunks / finalize の回復不能な 1 回エラー (非 2xx / 瞬断 retry 失敗) で
 *   MediaRecorder fallback に自動切替 (= 劣化. 409 特別扱い無し、3 連続カウント無し)。
 *   一時停止/再開は streaming session に束縛されるため本 transport のみ。
 * - batch route (start route='batch'. log/207): streaming session を張らず、劣化 fallback
 *   と同じ器 (mic + MediaRecorder + fallback commit) を最初から使う。
 *   「意図した batch / 劣化」の意味の区別は isIntendedBatch が持つ。
 *   batch の commit 先 (legacy batch upload) は onFallbackCommit として caller から
 *   注入される (= cache invalidation を page が持つため。client は features/yakureki/apis)。
 *
 * 層の地図 (物理分割は apps/legacy の生 field 直読みが消えた後 = log/173 §4 と同じトリガー。
 * 縫い目はこの区分で既に引いてある):
 * - 基盤 (transport 中立): 状態機械 (state / startGen / finishedSessionIds) /
 *   openMicAndRecorder / collectFallbackBlob / teardown / 救済 context (pendingBlob 5 点) /
 *   commit routing (fallbackCommitRef) / wireTrackMuteListener / retry / downloadBlob
 * - streaming transport: chunkQueue / worklet / awaitWorkletCloseAck / switchToFallback /
 *   wireInterruptionListeners (visibility + AudioContext) / pause / resume /
 *   adoptPausedSession / stop() の streaming path
 * - batch route: start() の batch 分岐 (基盤だけで完結)
 *
 * 責務外 (caller 側で扱う):
 * - 権限拒否時の UI / toast
 * - fallback commit 先の実体 (legacy batch 経路への POST/PATCH)
 * - polling (YakurekiDetail.transcription) / 話者分離トグル UI
 *
 * 設計: yakureki-back/log/143/design/stt_architecture.md §9.1 / §7.5 / §7.6 (streaming),
 *       yakureki-back/log/207 (batch 経路の開通と 1 bit の意味分離)
 */
export const useRecordingEngine = (
  args: RecordingEngineArgs = {}
): RecordingEngineReturn => {
  const [state, setStateInternal] = useState<RecordingEngineState>('idle')
  // stateRef は listener callback (= visibilitychange / track.muted / ctx.statechange) から
  // 最新 state を読むために併走させる. setState wrapper を介して常に setStateInternal と同時更新する.
  const stateRef = useRef<RecordingEngineState>('idle')
  const setState = useCallback((next: RecordingEngineState) => {
    stateRef.current = next
    setStateInternal(next)
  }, [])
  // ─── 救済 context (= failed 中の retry/DL 救済 UX を成立させる組) ─────
  // {pendingBlob, recordingEventIds (当事者判定), sessionIdRef (retry 宛先),
  //  isFallbackRef (retry 経路), error (文言 + isRetryHopeless)} の 5 点が核で、
  // どれか欠けると別患者への blob 誤露出 / retry 即死 / 文言消失になる. これに
  // pause 再送文脈 {pendingSubmitMode, segmentIndex} (log/185 F5) と、 開始 wipe で
  // 消える {isIntendedBatch (retry 後の文言経路), captureSettings (retry finalize の
  // 実効値運搬)} を加えた組全体が start() の snapshot/復元の単位.
  //
  // 不変条件: この組は必ず「組で」更新する. 失敗系 path (stop catch / fireInterruption /
  // retry catch) は核 5 点を同時に設定し、 start() は冒頭 snapshot → 失敗時に丸ごと復元、
  // resetInternal() は丸ごと破棄する. 部分更新は禁止.
  //
  // 構造上は単一の RescueContext object に統合するのが本来形だが、 apps/legacy が
  // 生 field (error / pendingBlob / hasPendingBlob / state) を直読みしている間は
  // field 形状を変えると互換 shim が必要になり複雑さが逆に増える. legacy 撤去で
  // 直読み消費者が消えた時点で統合すること (経緯: yakureki-front log/173 work-log/03 §4).
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [recordingSessionId, setRecordingSessionId] = useState<string | null>(
    null
  )
  const [recordingEventIds, setRecordingEventIdsInternal] = useState<string[]>(
    []
  )
  // recordingEventIdsRef / errorRef は start() の救済 snapshot が callback closure の
  // stale 値でなく最新値を読むために setter wrapper で併走させる (= stateRef と同じパターン).
  const recordingEventIdsRef = useRef<string[]>([])
  const setRecordingEventIds = useCallback((next: string[]) => {
    recordingEventIdsRef.current = next
    setRecordingEventIdsInternal(next)
  }, [])
  const [error, setErrorInternal] = useState<Error | null>(null)
  const errorRef = useRef<Error | null>(null)
  const setError = useCallback((next: Error | null) => {
    errorRef.current = next
    setErrorInternal(next)
  }, [])
  const [fallbackBlob, setFallbackBlob] = useState<Blob | null>(null)
  const [pendingBlob, setPendingBlob] = useState<Blob | null>(null)
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [isOffline, setIsOffline] = useState<boolean>(
    typeof navigator !== 'undefined' ? !navigator.onLine : false
  )
  // 低速経路 (legacy batch) での文字起こし進行中 flag. finalize response の
  // transcription_mode='batch_fallback' / fallback commit 成功で立てる.
  const [isSlowTranscription, setIsSlowTranscription] = useState(false)
  // 「意図した batch 経路」か (log/207 PR-FRONT ①). engine の 'fallback' 状態は
  // mic + MediaRecorder だけで録音している機構の名前で、「streaming が死んだ (= 劣化)」
  // という意味は本 flag が false の場合にだけ持つ. start() の batch 分岐で立てる.
  // ref は listener callback (= fireInterruption) が最新値を読むための併走 (stateRef と同パターン).
  const [isIntendedBatch, setIsIntendedBatchInternal] = useState(false)
  const isIntendedBatchRef = useRef(false)
  const setIsIntendedBatch = useCallback((next: boolean) => {
    isIntendedBatchRef.current = next
    setIsIntendedBatchInternal(next)
  }, [])
  // 確定済みセグメントの duration 合計 (log/185). pause 成功 / adopt (サーバ復元) で更新.
  const [stagedDurationS, setStagedDurationS] = useState(0)

  // online/offline reactivity. オフライン中は finalize mutation が pause されるため、
  // UI が「通信待ち」 を判別して DL 逃げ道を出せるよう live に signal を反映する.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onOnline = () => setIsOffline(false)
    const onOffline = () => setIsOffline(true)
    window.addEventListener('online', onOnline)
    window.addEventListener('offline', onOffline)
    return () => {
      window.removeEventListener('online', onOnline)
      window.removeEventListener('offline', onOffline)
    }
  }, [])

  // fallback 経路の commit point (= back に音声を流す責務) を 1 本に統合する.
  // - args.onFallbackCommit を渡したならそれを使う (= apps/latest / 完成形)
  // - 渡されない場合は defaultSurfaceCommit で fallbackBlob field に surface する
  //   (= apps/legacy useRecordAudio の useEffect 拾い流儀との backward-compat)
  // 内部 commit point を defaultSurfaceCommit 経由で必ず 1 つに保つことで、 stop() /
  // retry() の分岐を消し、 「state='completed' = commit 完了」 invariant を担保する.
  const defaultSurfaceCommit = useCallback(
    async (blob: Blob): Promise<void> => {
      setFallbackBlob(blob)
    },
    []
  )
  const fallbackCommitRef =
    useRef<(blob: Blob) => Promise<void>>(defaultSurfaceCommit)
  fallbackCommitRef.current = args.onFallbackCommit ?? defaultSurfaceCommit
  // pendingBlob と同じ参照を ref でも持つ (retry/downloadBlob の callback は state 更新の
  // 同期反映を待たずに値を読みたいため、 closure-stable な ref も並行更新する).
  const pendingBlobRef = useRef<Blob | null>(null)

  // 3 経路 interruption listener の cleanup 関数. teardown 内で drain して leak を防ぐ.
  const interruptionCleanupsRef = useRef<Array<() => void>>([])
  // switchToFallback (宣言順が先) から fireInterruption を呼ぶための間接参照
  // (log/185 検証 F3: マルチセグメント時は降格せず保全 finalize に倒す).
  // 多重発火 guard. 1 回 fire したら他経路 / visibilitychange の fallback は no-op.
  const firedInterruptionRef = useRef(false)

  // lifecycle resources (ref で保持して callback の closure 問題を避ける)
  const ctxRef = useRef<AudioContext | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null)
  const nodeRef = useRef<Linear16WorkletNode | null>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const fallbackChunksRef = useRef<BlobPart[]>([])

  // runtime state
  const sessionIdRef = useRef<string | null>(null)
  const isFallbackRef = useRef(false)
  // chunk POST の直列送信キュー (in-flight 常に 1 本 + まとめ送り).
  // backend は seq を dedup にしか使わず到着順で Chirp に feed するため、
  // 並行発射だと RTT スパイク時や瞬断 retry で到着順 (= 転写の時間軸) が
  // 崩れうる. 順序保証は送信側の責務 (詳細は chunkSendQueue.ts の doc).
  const chunkQueueRef = useRef<ChunkSendQueue | null>(null)
  // start() の生成トークン. cancel() (および新たな start() 自身) が ++ することで
  // in-flight の start を invalidate する. start() は各 await の後にこれを比較し、
  // 自分の myGen が最新でなければ作成済 backend SS を cancel して静かに return する.
  // これにより start() 中の cancel() が backend へ通知できないまま start が
  // 続行して 'recording' state に着地し、orphan SS が ~30s 残る race を防ぐ.
  const startGenRef = useRef(0)
  // worklet からの close-ack を待つ resolver. stop() が close postMessage の後で
  // この resolver を仕込み、worklet の onmessage が close-ack を受信した時点で
  // 解決される. ack 後は新規 chunk が来ないことが MessagePort FIFO で保証され、
  // 送信キューの drain を 1 回で安全に行える.
  const closeAckResolveRef = useRef<(() => void) | null>(null)
  // 一時停止セグメントの採番 (log/185). start で 0、pause 成功 / resume 応答で同期する.
  // pause retry は同 index 再送で冪等 (back は update_or_create upsert).
  const segmentIndexRef = useRef(0)
  // この engine instance が終端させた (完了 / 破棄 / expired) session の id 集合.
  // サーバ復元 (adoptPausedSession) は event キャッシュの staleness に晒される — 完了直後は
  // ページの terminal cleanup (reset→idle) の時点で poll 再取得前の古い
  // paused_streaming_session が残っており、 そのまま adopt すると終端済み session を
  // paused に引き戻すゾンビ復元になる. 終端の瞬間に id を記録し adopt を拒否する.
  const finishedSessionIdsRef = useRef<Set<string>>(new Set())
  // pendingBlob の再送先 (= retry() の経路分岐). 既定 'finalize'。 pause POST 失敗時のみ
  // 'pause' になり、 retry は同 segment_index で pause を再送する.
  const pendingSubmitModeRef = useRef<'finalize' | 'pause'>('finalize')
  // 収音制約の上書き (log/207 F). start() で params から受けて保持し、 resume の
  // マイク取り直しにも同じ値を適用する.
  const captureOverridesRef = useRef<
    RecordingStartParams['captureOverrides'] | undefined
  >(undefined)
  const audioBitsPerSecondRef = useRef<number | undefined>(undefined)
  // 収音の実効値 snapshot (= track.getSettings()). マイク確保のたび (= 各セグメント開始時.
  // pause→resume でも取り直す) に上書きし、 finalize は最終セグメントの値を back の
  // RecordingSession.capture_settings へ運ぶ (要求値ではなく実測値). セグメント間で
  // デバイスが変わると初回セグメントの値は残らない (実験B は単一セグメント前提で許容).
  const captureSettingsRef = useRef<Record<string, unknown> | undefined>(
    undefined
  )

  // 3 経路 interruption listener の一括解除. teardown のほか、 stop()/pause() の突入時にも
  // 先行して呼ぶ (log/185 検証 F1: finalize/pause POST 中に track.muted / ctx.statechange が
  // 発火すると fireInterruption の finalize が並行発射され state を破壊するため).
  const drainInterruptionListeners = useCallback(() => {
    interruptionCleanupsRef.current.forEach((fn) => {
      try {
        fn()
      } catch {
        // best effort
      }
    })
    interruptionCleanupsRef.current = []
  }, [])

  const teardown = useCallback(() => {
    // 3 経路 interruption listener を最初に drain (= leak fix). cancel/reset/通常 stop
    // すべての path から teardown が呼ばれるため、 ここで一括解除する.
    drainInterruptionListeners()
    // 送信キューの残 pending を捨てる (中断/cancel 経路で無意味な POST を止める.
    // 正常 stop() 経路では drain 済のため冪等 no-op).
    chunkQueueRef.current?.abort()
    try {
      nodeRef.current?.port.postMessage({ type: 'close' })
    } catch {
      // closed worklet はそのまま
    }
    try {
      nodeRef.current?.disconnect()
    } catch {
      // 未 connect もありうる
    }
    try {
      sourceRef.current?.disconnect()
    } catch {
      // same as above
    }
    try {
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop()
      }
    } catch {
      // best effort
    }
    streamRef.current?.getTracks().forEach((t) => t.stop())
    ctxRef.current?.close().catch(() => {
      // AudioContext.close は稀に失敗するが無視
    })
    nodeRef.current = null
    sourceRef.current = null
    streamRef.current = null
    ctxRef.current = null
    mediaRecorderRef.current = null
    setStream(null)
  }, [drainInterruptionListeners])

  const switchToFallback = useCallback(() => {
    // iOS Safari の多重発火対策 double guard: track.muted / ctx.statechange が先に
    // fireInterruption() を経て failed に倒れていれば、 visibilitychange 由来の
    // switchToFallback は no-op で抑止する (= 黄 banner → 赤 banner の flicker 防止).
    if (firedInterruptionRef.current) return
    if (isFallbackRef.current) return
    isFallbackRef.current = true
    console.warn(
      '[transcription-dispatch] useRecordingEngine.switchToFallback: streaming -> MediaRecorder fallback'
    )
    setState('fallback')

    // 送信キューを静かに打ち切る (fatal 起点の切替なら冪等 no-op).
    // 以降の chunk POST は無意味なので pending を捨てて送らない.
    chunkQueueRef.current?.abort()

    // worklet 経路のみ閉じる。MediaRecorder は既に並列録音中なのでそのまま継続。
    try {
      nodeRef.current?.port.postMessage({ type: 'close' })
      nodeRef.current?.disconnect()
      sourceRef.current?.disconnect()
    } catch {
      // best effort
    }
    nodeRef.current = null
    sourceRef.current = null

    // backend の streaming session を graceful close する (= 放棄せず後始末する).
    // これを怠ると runner は chunk 途絶のまま放置され、 ~10s 後に Google が
    // `Aborted: 409 Stream timed out after receiving no more client requests.` で stream を
    // 切り、 `ChirpSessionRunner failed` がフルトレースでログを汚染する (yakureki-back
    // log/159 work-log/02)。 reason='client_fallback' で意図的キャンセルと区別して記録する。
    // 409 (= runner 既死) は cancel 側が no-op 200 を返すので無害。通信不能時は従来通り
    // backend の orphan sweeper / Google ABORT に委ねる (best-effort)。
    const sid = sessionIdRef.current
    if (sid) {
      cancelStream(sid, 'client_fallback').catch(() => {
        // best-effort: 通知失敗は無視
      })
    }
  }, [setState])

  const collectFallbackBlob = useCallback(async (): Promise<Blob> => {
    const recorder = mediaRecorderRef.current
    const mimeType = recorder?.mimeType || 'audio/mp4'

    if (recorder && recorder.state !== 'inactive') {
      await new Promise<void>((resolve) => {
        recorder.addEventListener('stop', () => resolve(), { once: true })
        recorder.stop()
      })
    }
    return new Blob(fallbackChunksRef.current, { type: mimeType })
  }, [])

  // unrecoverable 中断 path. track.muted / AudioContext.statechange='suspended' から呼ぶ.
  //
  // 中断 (mic 奪われ / OS audio session 中断) では streaming は続行不能だが、 並列録音中の
  // MediaRecorder blob は中断直前まで有効. かつて cancelStream で SS を CANCELLED に倒して
  // blob を破棄していた (= 録音喪失 + hasPendingBlob=false で救済ボタン無し + 「既に送信済」
  // の誤案内) が、 nasu useAudioRecorder の onFallbackCommit→finalize 設計を移植して、
  // finalize(blob) で SOAP を保全する経路に変更する (stop() の streaming path と同型).
  // - 成功 → state='completed' に着地し SOAP 保全. RecordingInterruptedError を「中断した
  //   が保存済」 info notice として載せる (= MeetingBlock の interrupted-saved banner).
  // - 失敗 → pendingBlob を保持して state='failed'. RecordingFailedAlert の retry/DL 救済へ
  //   (hasPendingBlob=true).
  const fireInterruption = useCallback(
    (reason: InterruptionReason) => {
      if (firedInterruptionRef.current) return
      // 録音 hardware が生きている状態でのみ発火させる (log/185 検証 F1):
      // pausing/finalizing/paused 中に track.muted / ctx.statechange が発火すると、
      // 進行中の pause/finalize POST と並行で二重 finalize が走り state を破壊する.
      if (stateRef.current !== 'recording' && stateRef.current !== 'fallback') {
        return
      }
      firedInterruptionRef.current = true
      console.warn(
        '[transcription-dispatch] useRecordingEngine.fireInterruption: interruption detected, finalizing to preserve SOAP',
        { reason }
      )
      const sid = sessionIdRef.current
      void (async () => {
        setState('finalizing')
        // worklet 経路は既に死んでいる. MediaRecorder を止めて中断直前までの blob を確定.
        const blob = await collectFallbackBlob()
        teardown()
        // finalize / DL 前に唯一のコピーを必ず client 保持する.
        pendingBlobRef.current = blob
        setPendingBlob(blob)
        if (!sid) {
          if (isIntendedBatchRef.current) {
            // 意図した batch は streaming session を持たない (= finalize 先が無い).
            // stop() の fallback 経路と同じ commit (= legacy batch upload) で中断直前までの
            // 録音を SOAP に到達させ、 成功なら interrupted-saved notice に載せる.
            try {
              await fallbackCommitRef.current(blob)
              setIsSlowTranscription(true)
              pendingBlobRef.current = null
              setPendingBlob(null)
              setState('completed')
              setError(new RecordingInterruptedError(reason))
            } catch (commitErr) {
              console.error(
                '[transcription-dispatch] useRecordingEngine.fireInterruption: batch commit failed',
                commitErr
              )
              setError(toRecordingSubmitFailedError(commitErr))
              setState('failed')
            }
            return
          }
          // session 未確立 (= start 直後の極稀). 保全先 SS が無いので blob を握って DL 救済へ.
          setError(
            new Error(
              '録音が中断されました。 [音声をダウンロード] でファイルを保存してください。'
            )
          )
          setState('failed')
          return
        }
        try {
          const res = await finalizeStream(
            sid,
            blob,
            captureSettingsRef.current
          )
          // SOAP 保全成功. pendingBlob は破棄し、 中断 notice (info banner) のみ残す.
          if (res.transcription_mode === 'batch_fallback') {
            setIsSlowTranscription(true)
          }
          pendingBlobRef.current = null
          setPendingBlob(null)
          finishedSessionIdsRef.current.add(sid)
          setState('completed')
          setError(new RecordingInterruptedError(reason))
        } catch (err) {
          // finalize 失敗: blob を保持して retry/DL 救済に倒す.
          console.error(
            '[transcription-dispatch] useRecordingEngine.fireInterruption: finalize failed',
            err
          )
          setError(toRecordingSubmitFailedError(err))
          setState('failed')
        }
      })()
    },
    [setState, setError, teardown, collectFallbackBlob]
  )

  // track.muted (= mic 奪われ) の unrecoverable 中断 listener を wire する.
  // streaming (wireInterruptionListeners 経由) と batch 経路 (start の batch 分岐が直接
  // 呼ぶ. AudioContext が無く visibility 降格も意味を持たないため、これだけを張る) の共通部.
  const wireTrackMuteListener = useCallback(
    (rawStream: MediaStream) => {
      const track = rawStream.getAudioTracks()[0]
      if (!track) return
      const onMute = () => fireInterruption('track-mute')
      track.addEventListener('mute', onMute)
      interruptionCleanupsRef.current.push(() =>
        track.removeEventListener('mute', onMute)
      )
    },
    [fireInterruption]
  )

  // 3 経路の interruption listener を MediaStream + AudioContext に wire し、
  // cleanup 関数を interruptionCleanupsRef に push する.
  //
  // - visibilitychange (= recoverable): 50ms 待ち + double guard で fallback に切替.
  //   iOS Safari で 3 経路がほぼ同時発火する際の「黄→赤 flicker」 を防ぐため、
  //   visibility 経路は遅延 + firedInterruptionRef guard で抑止する.
  // - track.muted (= unrecoverable): mic 奪われ. fireInterruption で teardown.
  // - AudioContext.statechange='suspended'|'interrupted' (= unrecoverable): OS audio session 中断.
  const wireInterruptionListeners = useCallback(
    (rawStream: MediaStream, ctx: AudioContext) => {
      // (a) visibilitychange — recoverable 経路 (= switchToFallback)
      const onVisibility = () => {
        if (typeof document === 'undefined') return
        if (!document.hidden) return
        if (stateRef.current !== 'recording') return
        // マルチセグメント (一時停止後) はタブ切替に反応しない (log/185).
        // #728 (visibility fallback 撤去) の burn-in 実測で「タブを離れても streaming は
        // 背後で継続し、 欠けは GS-3 が救済する」ことが確認済み. 疑義照会後の録音中に
        // タブを離れる操作は日常であり、 降格 (= 前セグメントの commit 経路が変わる) も
        // 保全 finalize (= 録音終了) もユーザー体験を壊すため、 単に何もしない.
        // #728 本体のリリース後は単一セグメントもこの挙動に揃う.
        if (segmentIndexRef.current > 0) return
        // hidden で即降格せず grace window 待つ. 短時間の tab 切替で復帰すれば no-op、
        // grace 超過しても hidden のままなら fallback に倒す (= 過剰検知の緩和).
        // iOS で 3 経路ほぼ同時発火する際は、 grace 中に unrecoverable (track.muted /
        // ctx state) が先に走れば firedInterruptionRef guard で skip される.
        setTimeout(() => {
          if (firedInterruptionRef.current) return
          if (stateRef.current !== 'recording') return
          // grace 中に pause → resume でマルチセグメント化した場合も降格しない.
          if (segmentIndexRef.current > 0) return
          // 復帰した (= grace 内に戻ってきた) なら降格しない.
          if (typeof document !== 'undefined' && !document.hidden) return
          switchToFallback()
        }, VISIBILITY_GRACE_MS)
      }
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', onVisibility)
        interruptionCleanupsRef.current.push(() =>
          document.removeEventListener('visibilitychange', onVisibility)
        )
      }

      // (b) track.muted — unrecoverable 経路 (= mic 奪われ)
      wireTrackMuteListener(rawStream)

      // (c) AudioContext.statechange='suspended'|'interrupted' — unrecoverable 経路
      const onCtxStateChange = () => {
        const s = ctx.state as string
        if (s === 'suspended' || s === 'interrupted') {
          fireInterruption('audio-context-suspended')
        }
      }
      ctx.addEventListener('statechange', onCtxStateChange)
      interruptionCleanupsRef.current.push(() =>
        ctx.removeEventListener('statechange', onCtxStateChange)
      )
    },
    [switchToFallback, fireInterruption, wireTrackMuteListener]
  )

  // worklet に close を送り ack を待つ (= 送信キューを安全に drain できる状態にする).
  // stop() / pause() が共用する. ack が返らない場合は 1s timeout で先へ進む.
  const awaitWorkletCloseAck = useCallback(async (): Promise<void> => {
    const node = nodeRef.current
    if (!node) return
    const closeAcked = new Promise<void>((resolve) => {
      closeAckResolveRef.current = resolve
      // worklet 側で何らかの理由 (既に teardown 等) で ack が返らない場合の fallback.
      // (audio thread render quantum は ms オーダーで通常は数 ms 以内に届く)
      setTimeout(() => {
        if (closeAckResolveRef.current === resolve) {
          console.warn(
            '[transcription-dispatch] useRecordingEngine: close-ack timeout, proceeding to drain'
          )
          closeAckResolveRef.current = null
          resolve()
        }
      }, 1000)
    })
    try {
      node.port.postMessage({ type: 'close' })
    } catch {
      // worklet すでに閉じ. ack を待たずに進める.
      const resolve = closeAckResolveRef.current
      closeAckResolveRef.current = null
      resolve?.()
    }
    await closeAcked
  }, [])

  // マイク確保 + MediaRecorder 並列起動 (= ローカル録音パイプラインの土台).
  // streaming (openLocalPipeline がこの上に worklet を張る) と、将来の batch 経路
  // (worklet 無しでこの土台だけを使う) の共通部として切り出している.
  // 'invalidated' は cancel() / 後続 start() の世代無効化による静かな撤退 (資源は解放済み).
  const openMicAndRecorder = useCallback(
    async (myGen: number): Promise<MediaStream | 'invalidated'> => {
      // マイク確保 (streaming 最適化: 16kHz mono)
      // echoCancellation を false にしている理由:
      //   Chrome の AEC (acoustic echo cancellation) は録音開始から 2-5 秒の adaptation
      //   期間を必要とし、その間に音声が減衰される / echo が漏れる挙動が well-documented.
      //   薬局-患者の会話録音はスピーカー出力を mic に流す経路がないため AEC は無価値で、
      //   起動時減衰のデメリットだけが効いていた (4/30 staging QA 実測). 明示的に false.
      // autoGainControl も false: adaptation を伴う処理で先頭音量を歪ませる可能性がある.
      // noiseSuppression は維持: adaptation 影響が軽微で薬局環境のノイズ除去メリットが大きい.
      // 2 値とも custom_setting の override (log/207 F) で上書きできる
      // (noiseSuppression はユーザー設定画面から、autoGainControl は admin 専用).
      // key 無し・null は上記の既定値.
      const overrides = captureOverridesRef.current
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: false,
          noiseSuppression: overrides?.noiseSuppression ?? true,
          autoGainControl: overrides?.autoGainControl ?? false,
        },
      })
      if (myGen !== startGenRef.current) {
        stream.getTracks().forEach((t) => t.stop())
        return 'invalidated'
      }
      streamRef.current = stream
      setStream(stream)
      // 収音の実効値 snapshot (要求値ではなく実測値. 実験B の交絡確認用に finalize が運ぶ).
      // getSettings 非対応環境 (旧ブラウザ等) では undefined のまま = 送らない.
      // label (= マイク機種名) は getSettings に含まれないため track から足す.
      // UA は back が request header から付与する.
      try {
        const track = stream.getAudioTracks()[0]
        const settings = track?.getSettings?.() as
          | Record<string, unknown>
          | undefined
        captureSettingsRef.current = settings
          ? {
              ...settings,
              label: track.label,
            }
          : undefined
      } catch {
        captureSettingsRef.current = undefined
      }

      // MediaRecorder を並列起動 (= このセグメントの全体音声を確保. streaming 失敗時の
      // fallback 用と finalize/pause multipart 同梱用を兼ねる).
      // mimeType は AAC/MP4 を優先し、ブラウザ未サポート時は default (WebM/Opus) に任せる。
      let mediaRecorder: MediaRecorder
      try {
        mediaRecorder = new MediaRecorder(stream, {
          mimeType: 'audio/mp4',
          audioBitsPerSecond: audioBitsPerSecondRef.current,
        })
      } catch {
        mediaRecorder = new MediaRecorder(stream)
      }
      fallbackChunksRef.current = []
      mediaRecorder.ondataavailable = (e: BlobEvent) => {
        if (e.data.size > 0) fallbackChunksRef.current.push(e.data)
      }
      // timeslice = 100ms.
      // 引数なしの mediaRecorder.start() では `ondataavailable` が stop() 時にしか
      // 発火せず、stop 前に AudioWorklet 経由の chunk POST が失敗して fallback に
      // 切り替えた場合、それ以降の音声が `fallbackChunksRef` に集まらず blob が空
      // (またはごく短い) のまま batch POST されていた。 100ms ごとに ondataavailable
      // を発火させることで、fallback に倒れた瞬間以降の録音を逐次蓄積でき、blob が
      // 完全な状態で確保される。
      // 100ms は AudioWorklet の chunk emit 周期 (CHUNK_INTERVAL_MS) と同期させ、
      // 万一の比較・debug 時にタイムラインを揃えやすくしている。
      mediaRecorder.start(100)
      mediaRecorderRef.current = mediaRecorder
      return stream
    },
    []
  )

  // マイク〜MediaRecorder〜AudioWorklet〜interruption listener のローカル録音パイプラインを
  // 構築し 'recording' へ遷移する (start / resume 共用. log/185).
  // 前提: 呼出側が chunkQueueRef を session に bind 済み (= onmessage が enqueue する先).
  // 'invalidated' は cancel() / 後続 start() の世代無効化による静かな撤退 (資源は解放済み).
  const openLocalPipeline = useCallback(
    async (myGen: number): Promise<'ok' | 'invalidated'> => {
      const acquired = await openMicAndRecorder(myGen)
      if (acquired === 'invalidated') return 'invalidated'
      const stream = acquired

      // AudioContext + worklet
      const ctx = new AudioContext({ sampleRate: 16000 })
      ctxRef.current = ctx
      await ctx.audioWorklet.addModule('/worklets/pcm-linear16-processor.js')
      if (myGen !== startGenRef.current) {
        // ここまでで stream / MediaRecorder / ctx を握っているので teardown で解放.
        teardown()
        return 'invalidated'
      }
      const node = new AudioWorkletNode(
        ctx,
        'pcm-linear16-processor'
      ) as Linear16WorkletNode
      nodeRef.current = node

      const src = ctx.createMediaStreamSource(stream)
      sourceRef.current = src
      src.connect(node)
      // destination には繋がない (再生しない)

      node.port.onmessage = (ev: MessageEvent<Linear16WorkletMessage>) => {
        const msg = ev.data
        if (msg.type === 'close-ack') {
          // stop()/pause() が仕込んだ resolver を起こして deterministic drain に進ませる.
          const resolve = closeAckResolveRef.current
          closeAckResolveRef.current = null
          resolve?.()
          return
        }
        if (msg.type !== 'chunk') return
        if (isFallbackRef.current) return

        // 直列送信キューに積むだけ (POST は sender loop が 1 本ずつ実行).
        // 瞬断 retry (同 seq/同 payload 1 回) と回復不能時の fallback 降格は
        // キュー内部で行われる (chunkSendQueue.ts).
        chunkQueueRef.current?.enqueue(msg.buffer)
      }

      // 3 経路 interruption listener を wire (= recording 開始直前).
      // teardown / fireInterruption / switchToFallback の経路で interruptionCleanupsRef
      // に積んだ cleanup が drain される.
      wireInterruptionListeners(stream, ctx)

      setState('recording')
      return 'ok'
    },
    [openMicAndRecorder, setState, teardown, wireInterruptionListeners]
  )

  const start = useCallback(
    async (params: RecordingStartParams): Promise<boolean> => {
      // この start() invocation の世代. cancel() / 後続 start() が startGenRef.current を
      // ++ すると myGen と一致しなくなり、await 後に invalidated として静かに撤退できる.
      const myGen = ++startGenRef.current

      // 前回失敗 session の救済 context. 救済 UX は blob 単体でなく
      // {blob, 当事者 event ids, retry 宛先 session, retry 経路, error} の組で初めて成立する
      // (どれか欠けると別患者への blob 誤露出 / retry 即死 / 文言消失になる) ため、 組で
      // snapshot する. 「新しい録音の開始」 は救済の放棄を意味するので直下で wipe するが、
      // 開始に失敗した場合 (= 録音は 1 秒も始まっていない = 放棄は成立しない) は catch で
      // 丸ごと復元し、 二度撮り不能の医療 record をマイク許可拒否などで無言喪失させない.
      const rescue = pendingBlobRef.current
        ? {
            blob: pendingBlobRef.current,
            eventIds: recordingEventIdsRef.current,
            sessionId: sessionIdRef.current,
            isFallback: isFallbackRef.current,
            error: errorRef.current,
            // log/185 検証 F5: pause 失敗の救済は「同 segment_index で pause を再送する」
            // 文脈まで含めて初めて成立する. これらを復元しないと retry() が finalize モード
            // に劣化し、 PAUSED セッションをセグメント blob で誤 finalize する.
            pendingSubmitMode: pendingSubmitModeRef.current,
            segmentIndex: segmentIndexRef.current,
            // 開始 wipe で消える意味 bit と実効値 snapshot も組に含める. 復元しないと
            // retry 成功後の生成中バナーが劣化文言になり (batch 救済)、 retry の
            // finalize が capture_settings を落とす (streaming 救済).
            isIntendedBatch: isIntendedBatchRef.current,
            captureSettings: captureSettingsRef.current,
          }
        : null

      setState('starting')
      setError(null)
      setFallbackBlob(null)
      setIsSlowTranscription(false)
      setIsIntendedBatch(false)
      // 救済 blob の破棄 (= 開始成功時の確定動作). 保持を続けると次の stop()/reset() が
      // 旧 blob を新 session の音声として誤送出しうる. 失敗時は上記 rescue から復元される.
      setPendingBlob(null)
      pendingBlobRef.current = null
      setSessionId(null)
      setRecordingSessionId(null)
      setRecordingEventIds([])
      setStream(null)
      sessionIdRef.current = null
      isFallbackRef.current = false
      firedInterruptionRef.current = false
      setStagedDurationS(0)
      pendingSubmitModeRef.current = 'finalize'
      captureOverridesRef.current = params.captureOverrides
      audioBitsPerSecondRef.current = params.audioBitsPerSecond
      captureSettingsRef.current = undefined
      chunkQueueRef.current?.abort()
      chunkQueueRef.current = null
      fallbackChunksRef.current = []

      // 既に backend に作成された SS を best-effort で cancel する helper.
      // start() の途中 await で invalidated を検知した時に使う.
      const cancelOrphanSession = (sessionId: string) => {
        console.info(
          '[transcription-dispatch] useRecordingEngine.start: invalidated mid-flight, notifying backend cancel',
          { session_id: sessionId }
        )
        cancelStream(sessionId)
          .then((res) => {
            // start 中の破棄 (「中断して移動」) では cancel() が session 無しで即 resolve するため、
            // back が消したフリー録音の id はここでしか受け取れない. 一覧の永続キャッシュから外して
            // おけば、 次の delta 同期 (2 秒) で無名の薬歴が消える (ADR-0039).
            removeYakurekiFromLocalCache(res.deleted_event_ids)
          })
          .catch(() => {
            // 410/409/network いずれも UI 上は cancel 完了に倒す
          })
      }

      try {
        // 意図した batch 経路 (log/207 PR-FRONT ①): streaming session を張らず、
        // fallback の器 (mic + MediaRecorder + fallback commit) を最初から使う.
        // sessionId は生まれない (= pause 不可・live transcript 無し). 停止時は stop() の
        // fallback path が onFallbackCommit (= legacy batch upload) へ blob を流す.
        if (params.route === 'batch') {
          setIsIntendedBatch(true)
          isFallbackRef.current = true
          // 当事者判定 (deriveViewStatus の engineEventIds) 用. batch は event 前提
          // (フリー録音は未開通 = caller が streaming に固定する. log/192 §3.4).
          setRecordingEventIds(params.yakurekiIds)
          segmentIndexRef.current = 0
          console.info(
            '[transcription-dispatch] useRecordingEngine.start: intended batch route (no streaming session)',
            { yakureki_ids: params.yakurekiIds }
          )
          const acquired = await openMicAndRecorder(myGen)
          if (acquired === 'invalidated') return true
          // 中断 listener は track.muted のみ. AudioContext が無く、 visibility 降格も
          // 意味を持たない (MediaRecorder はタブ非表示でも録音を継続する).
          wireTrackMuteListener(acquired)
          setState('fallback')
          return true
        }

        // 1. session start
        const session = await startStreamingSession({
          yakureki_ids: params.yakurekiIds,
          pharmacist_id: params.pharmacistId,
          memo_items: params.memoItems,
          has_companion_or_substitute_map: params.hasCompanionOrSubstituteMap,
          // preset (auto/deep) はそのまま history_policy として送る。
          history_policy: params.preset,
          is_free_recording: params.isFreeRecording,
          label: params.label,
          session_type: params.sessionType,
        })
        // cancel() / 別 start() が await 中に走ったケース. backend には SS が
        // 出来てしまっているので必ず cancel を投げ、setState 等は行わない
        // (cancel() が既に resetInternal で 'idle' に戻している).
        if (myGen !== startGenRef.current) {
          cancelOrphanSession(session.session_id)
          return true
        }
        setSessionId(session.session_id)
        setRecordingSessionId(session.recording_session_id)
        setRecordingEventIds(session.yakureki_ids)
        sessionIdRef.current = session.session_id
        // chunk 直列送信キューを session に bind して生成. 回復不能失敗
        // (非 transient / 再送失敗) は従来同様 MediaRecorder fallback に倒す.
        chunkQueueRef.current = new ChunkSendQueue({
          send: (seq, b64) => postAudioChunk(session.session_id, seq, b64),
          onFatal: () => {
            console.warn(
              '[transcription-dispatch] useRecordingEngine: chunk POST 回復不能エラー → switchToFallback'
            )
            switchToFallback()
          },
        })
        console.info(
          '[transcription-dispatch] useRecordingEngine.start: session started',
          {
            session_id: session.session_id,
            recording_session_id: session.recording_session_id,
            yakureki_ids: session.yakureki_ids,
          }
        )

        // 2-4. ローカル録音パイプライン構築 (マイク → MediaRecorder → worklet → listener.
        // resume と共用の openLocalPipeline に集約. 詳細コメントはそちら).
        segmentIndexRef.current = 0
        const piped = await openLocalPipeline(myGen)
        if (piped === 'invalidated') {
          cancelOrphanSession(session.session_id)
          return true
        }
        return true
      } catch (err) {
        teardown()
        // cancel() が割り込んだ場合は state は既に 'idle' なのでエラーを surface しない.
        if (myGen !== startGenRef.current) {
          return true
        }
        console.error(
          '[transcription-dispatch] useRecordingEngine.start: failed to start',
          err
        )
        if (rescue) {
          // 開始に失敗したので旧救済の放棄は成立しない. 救済 context を丸ごと復元して
          // start 前の failed に正確に巻き戻す (部分復元だと blob と当事者 ids / retry
          // 宛先がずれ、 別患者ページへの blob 誤露出や retry 即死を生む).
          // この start 自体の失敗理由は返り値 false で caller に通知させる.
          pendingBlobRef.current = rescue.blob
          setPendingBlob(rescue.blob)
          setRecordingEventIds(rescue.eventIds)
          setSessionId(rescue.sessionId)
          sessionIdRef.current = rescue.sessionId
          isFallbackRef.current = rescue.isFallback
          pendingSubmitModeRef.current = rescue.pendingSubmitMode
          segmentIndexRef.current = rescue.segmentIndex
          setIsIntendedBatch(rescue.isIntendedBatch)
          captureSettingsRef.current = rescue.captureSettings
          setError(rescue.error)
          setState('failed')
          return false
        }
        const e = err instanceof Error ? err : new Error(String(err))
        setError(e)
        setState('failed')
        return false
      }
    },
    [
      setState,
      setError,
      setRecordingEventIds,
      setIsIntendedBatch,
      switchToFallback,
      teardown,
      openLocalPipeline,
      openMicAndRecorder,
      wireTrackMuteListener,
    ]
  )

  const stop = useCallback(async () => {
    // 進行中の finalize/pause POST と interruption listener の並行発射を防ぐため、
    // 停止系に入る時点で listener を先に解除する (log/185 検証 F1. fireInterruption 側の
    // state guard と二重の防御).
    drainInterruptionListeners()

    // paused 経路 (log/185): engine はローカル資源を何も持っていない. blob 無し finalize を
    // 投げるだけで、 back が staged セグメントのみを concat して SOAP を生成する.
    // 失敗しても録音データはサーバ側に安全に存在するため failed に倒さず 'paused' に戻す
    // (= error banner を出しつつ [完了] の再操作で retry できる).
    if (stateRef.current === 'paused') {
      const sid = sessionIdRef.current
      if (!sid) {
        setState('completed')
        return
      }
      console.info(
        '[transcription-dispatch] useRecordingEngine.stop: paused path (finalize without blob)',
        { session_id: sid }
      )
      setState('finalizing')
      try {
        const res = await finalizeStream(
          sid,
          undefined,
          captureSettingsRef.current
        )
        if (res.transcription_mode === 'batch_fallback') {
          setIsSlowTranscription(true)
        }
        finishedSessionIdsRef.current.add(sid)
        setState('completed')
      } catch (err) {
        console.error(
          '[transcription-dispatch] useRecordingEngine.stop: paused finalize failed',
          err
        )
        setError(toRecordingSubmitFailedError(err))
        setState('paused')
      }
      return
    }

    // fallback 経路: MediaRecorder を停止し、 fallbackCommitRef を 1 度 await する.
    // commit 経路は内部で必ず 1 本化されている (= apps/latest の onFallbackCommit /
    // apps/legacy 互換の defaultSurfaceCommit のいずれか).
    if (isFallbackRef.current) {
      console.info(
        '[transcription-dispatch] useRecordingEngine.stop: fallback path'
      )
      setState('finalizing')
      const blob = await collectFallbackBlob()
      teardown()
      // commit を await する前に blob を必ず client 保持する. オフライン時 commit は
      // TanStack Query (networkMode='online') が mutation を pause し await が完走しない
      // ため、 ここで保持しておかないとタブ閉じで唯一のコピーを失う. 復帰で auto-resume
      // して成功すれば下の completed 経路で pendingBlob を破棄する.
      pendingBlobRef.current = blob
      setPendingBlob(blob)
      try {
        const sid = sessionIdRef.current
        if (sid && segmentIndexRef.current > 0) {
          // salvage finalize (log/185): マルチセグメントの fallback commit は legacy 経路では
          // staged (確定済み前セグメント) を運べないため、 finalize (blob 同梱) へ遅延 commit
          // する. back は降格時の cancel(client_fallback) 済み CANCELLED を salvage として
          // 受理し、 staged concat → GS-3 whole-file 転写で全区間を SOAP に到達させる.
          // (単一セグメントの同経路への統一は salvage burn-in 後の後続 PR = log/185 §2 段階計画)
          console.info(
            '[transcription-dispatch] useRecordingEngine.stop: salvage finalize (multi-segment fallback)',
            { session_id: sid, segment_index: segmentIndexRef.current }
          )
          const res = await finalizeStream(
            sid,
            blob,
            captureSettingsRef.current
          )
          if (res.transcription_mode === 'batch_fallback') {
            setIsSlowTranscription(true)
          }
        } else {
          await fallbackCommitRef.current(blob)
          // client 起点 fallback の commit 成功 = legacy batch (低速) での文字起こし確定.
          setIsSlowTranscription(true)
        }
        pendingBlobRef.current = null
        setPendingBlob(null)
        if (sessionIdRef.current) {
          finishedSessionIdsRef.current.add(sessionIdRef.current)
        }
        setState('completed')
      } catch (err) {
        // commit 失敗時は blob を pendingBlob に倒し、 retry/DL UX で救済する.
        // 服薬指導録音は二度撮り不能の医療 record 制約のため、 blob を必ず client に
        // 保持する.
        console.error(
          '[transcription-dispatch] useRecordingEngine.stop: fallback commit failed',
          err
        )
        pendingBlobRef.current = blob
        setPendingBlob(blob)
        setError(toRecordingSubmitFailedError(err))
        setState('failed')
      }
      return
    }

    // streaming 経路
    console.info(
      '[transcription-dispatch] useRecordingEngine.stop: streaming path (finalize multipart)',
      { session_id: sessionIdRef.current }
    )
    setState('finalizing')

    // 1. worklet に close を送り、close-ack を待つ.
    // worklet 側は `_closed=true` を立てた直後に `{type:'close-ack'}` を返す.
    // MessagePort は FIFO なので、ack より前に worklet が emit した chunk は
    // すべて main の onmessage で送信キューに積まれ済であることが保証される.
    // → ack 受信後は queue.drain() 1 回で完全 drain できる.
    await awaitWorkletCloseAck()

    // 2. 送信キューを掃き切る. close-ack 受信後は新規 chunk が来ないので、
    //    drain 1 回で送信待ち + in-flight (瞬断 retry 中も含む) が完全に空になる.
    //    fatal (= fallback 降格) で打ち切られた場合も resolve する.
    await chunkQueueRef.current?.drain()

    const sid = sessionIdRef.current
    if (!sid) {
      teardown()
      setState('completed')
      return
    }

    // 3. MediaRecorder を停止して並列録音 blob を確定 (finalize に同梱するため先に取得).
    const blob = await collectFallbackBlob()

    // finalize を await する前に blob を必ず client 保持する. オフライン時 finalize mutation は
    // TanStack Query (networkMode='online') が pause し await が完走しないため、 ここで保持して
    // おかないとタブ閉じで唯一のコピーを失う. 成功すれば下の completed 経路で破棄する.
    pendingBlobRef.current = blob
    setPendingBlob(blob)

    // 4. finalize (SENTINEL 送信 + audio 同梱). backend は 1 endpoint で SOAP pipeline
    //    kick + persist_stream_audio (S3/GCS/post_batch) を kick する.
    try {
      const res = await finalizeStream(sid, blob, captureSettingsRef.current)
      // backend が batch fallback (= SS FAILED 由来 GS-3 / chunk 欠落検知) を宣言したら
      // 低速文字起こし notice を立てる (SOAP 生成完了まで UI が表示).
      if (res.transcription_mode === 'batch_fallback') {
        setIsSlowTranscription(true)
      }
    } catch (err) {
      // finalize 失敗時: 服薬指導録音は二度撮り不可能の医療 record 制約のため、
      // 'failed' state + pendingBlob で client 側に Blob を保持して
      // RecordingFailedAlert の retry/DL 救済導線に倒す.
      // (legacy batch への自動 fallback は削除. caller が screen を抜けると blob ロスト
      // するリスクのほうが大きいため、 ユーザー操作で retry/DL を確定させる UX に統一.)
      console.error(
        '[transcription-dispatch] useRecordingEngine.stop: finalize failed',
        err
      )
      teardown()
      pendingBlobRef.current = blob
      setPendingBlob(blob)
      setError(toRecordingSubmitFailedError(err))
      setState('failed')
      return
    }

    teardown()
    pendingBlobRef.current = null
    setPendingBlob(null)
    finishedSessionIdsRef.current.add(sid)
    setState('completed')
  }, [
    awaitWorkletCloseAck,
    collectFallbackBlob,
    drainInterruptionListeners,
    setState,
    setError,
    teardown,
  ])

  // 内部 state を idle に戻す共通処理 (backend 通知なし).
  // cancel() / reset() の両方から呼ばれる. backend への通知は cancel() 側のみで行う.
  const resetInternal = useCallback(() => {
    teardown()
    // stop() が close-ack を待っている最中に外側から reset/cancel が走った場合、
    // 待ち続けないよう resolver を起こす (timeout fallback もあるが即時解放のため).
    const ackResolve = closeAckResolveRef.current
    closeAckResolveRef.current = null
    ackResolve?.()
    setState('idle')
    setError(null)
    setFallbackBlob(null)
    // isSlowTranscription はここでは消さない: reset() は「次の録音に備える engine cleanup」
    // であり (IndexPage は engine='completed' 直後に terminal cleanup として自動で呼ぶ)、
    // server 側では低速 batch 生成がまだ進行中. ここで消すと generating 中の低速 notice が
    // 巻き添えで消える. クリアは start() (新 session) と cancel() (batch が走らない中断) のみ.
    setPendingBlob(null)
    pendingBlobRef.current = null
    setSessionId(null)
    setRecordingSessionId(null)
    setRecordingEventIds([])
    setStream(null)
    sessionIdRef.current = null
    isFallbackRef.current = false
    firedInterruptionRef.current = false
    segmentIndexRef.current = 0
    setStagedDurationS(0)
    pendingSubmitModeRef.current = 'finalize'
    chunkQueueRef.current?.abort()
    chunkQueueRef.current = null
    fallbackChunksRef.current = []
  }, [setState, setError, setRecordingEventIds, teardown])

  // 一時停止 (log/185 セグメント確定方式): stop() streaming 経路と同じ順序でこのセグメントを
  // 閉じ (close-ack → drain → blob 確定)、 POST /pause/ (multipart) で back に確定させる.
  // 成功後は teardown で全解放し 'paused' へ. paused の正本はサーバ側なので、 以降タブ閉じ・
  // reload・患者切替のいずれでも録音データは失われない (復元はサーバ由来 = event 詳細の
  // paused_streaming_session. sessionStorage 非依存).
  const pause = useCallback(async (): Promise<'paused' | 'failed' | 'noop'> => {
    // streaming 'recording' 中のみ. fallback 中は streaming session が cancel 済 (=
    // client_fallback) で pause が成立しない (UI も showPauseCta を stable 時に限定).
    // guard 拒否は 'noop' (= 二重押下. 失敗 toast を出さない, 検証 B7).
    if (stateRef.current !== 'recording' || isFallbackRef.current) {
      return 'noop'
    }
    const sid = sessionIdRef.current
    if (!sid) return 'noop'
    const segmentIndex = segmentIndexRef.current

    // cancel() との協調 (log/185 検証 N4): 世代トークンに参加し、 pause 処理中に cancel が
    // 走ったら以降の状態変更 (paused 化 / sessionStorage 復活 / failed 化) を行わず静かに
    // 撤退する. cancel 側が backend 通知 + resetInternal 済のため、 back が PAUSED で
    // 確定していても cancel (PAUSED 許容) が CANCELLED に畳んでいる.
    const myGen = ++startGenRef.current

    console.info(
      '[transcription-dispatch] useRecordingEngine.pause: pausing segment',
      { session_id: sid, segment_index: segmentIndex }
    )
    setState('pausing')
    // 進行中の pause POST と interruption listener の並行発射を防ぐ (log/185 検証 F1).
    drainInterruptionListeners()

    await awaitWorkletCloseAck()
    await chunkQueueRef.current?.drain()
    const blob = await collectFallbackBlob()
    if (myGen !== startGenRef.current) return 'noop'
    // POST 前に唯一のコピーを client 保持する (オフライン/失敗時の retry・DL 救済用).
    pendingBlobRef.current = blob
    setPendingBlob(blob)

    try {
      const res = await pauseStreamingSession(sid, blob, segmentIndex)
      if (myGen !== startGenRef.current) return 'noop'
      teardown()
      pendingBlobRef.current = null
      setPendingBlob(null)
      segmentIndexRef.current = res.next_segment_index
      setStagedDurationS(res.staged_duration_s)
      setState('paused')
      return 'paused'
    } catch (err) {
      // pause POST 失敗: blob を保持して retry 救済に倒す. back の upsert は同 segment_index の
      // 再送で冪等なので、 retry() は pause を再送する (pendingSubmitModeRef='pause').
      if (myGen !== startGenRef.current) return 'noop'
      console.error(
        '[transcription-dispatch] useRecordingEngine.pause: pause POST failed',
        err
      )
      teardown()
      pendingSubmitModeRef.current = 'pause'
      setError(toRecordingSubmitFailedError(err))
      setState('failed')
      return 'failed'
    }
  }, [
    awaitWorkletCloseAck,
    collectFallbackBlob,
    drainInterruptionListeners,
    setError,
    setState,
    teardown,
  ])

  // 一時停止からの再開 (log/185): サーバの PAUSED→ACTIVE を戻してから、 start() と同じ
  // ローカルパイプラインで次セグメントの録音に入る. 失敗しても 'paused' に留まる
  // (= サーバ側データは無傷. error banner を出して再操作を待つ).
  const resume = useCallback(async (): Promise<
    'resumed' | 'failed' | 'expired' | 'noop'
  > => {
    // guard 拒否は 'noop' (= 二重押下. 失敗 toast を出さない, 検証 B7).
    if (stateRef.current !== 'paused') return 'noop'
    const sid = sessionIdRef.current
    if (!sid) return 'noop'

    // 世代を進めて古い start/resume を無効化 (start() と同じ invalidation 機構に参加).
    const myGen = ++startGenRef.current
    console.info(
      '[transcription-dispatch] useRecordingEngine.resume: resuming session',
      { session_id: sid }
    )
    setState('resuming')
    setError(null)
    isFallbackRef.current = false
    firedInterruptionRef.current = false
    fallbackChunksRef.current = []

    try {
      const res = await resumeStreamingSession(sid)
      if (myGen !== startGenRef.current) {
        // cancel() 割込. cancel() 側が backend 通知 + reset 済なので静かに撤退.
        return 'resumed'
      }
      if (typeof res.segment_index === 'number') {
        segmentIndexRef.current = res.segment_index
      }
      chunkQueueRef.current = new ChunkSendQueue({
        send: (seq, b64) => postAudioChunk(sid, seq, b64),
        onFatal: () => {
          console.warn(
            '[transcription-dispatch] useRecordingEngine: chunk POST 回復不能エラー → switchToFallback'
          )
          switchToFallback()
        },
      })
      const piped = await openLocalPipeline(myGen)
      if (piped === 'invalidated') return 'resumed'
      return 'resumed'
    } catch (err) {
      if (myGen !== startGenRef.current) return 'resumed'
      console.error(
        '[transcription-dispatch] useRecordingEngine.resume: failed',
        err
      )
      teardown()
      chunkQueueRef.current?.abort()
      chunkQueueRef.current = null
      const normalized = toRecordingSubmitFailedError(err)
      if (
        normalized instanceof RecordingSubmitFailedError &&
        normalized.isRetryHopeless
      ) {
        // サーバ側 session が消滅している (destroy 済等 = 4xx/409). paused に留めると
        // [再開]/[完了] の双方が失敗し続ける袋小路になる (log/185 検証 F6) ため idle に戻す.
        // stale な event キャッシュからの再 adopt も terminal 記録で拒否する.
        finishedSessionIdsRef.current.add(sid)
        resetInternal()
        return 'expired'
      }
      // 一時的な失敗 (network / マイク許可拒否). サーバ側データは無傷なので 'paused' に
      // 戻して error を surface する (再操作可能).
      setError(normalized)
      setState('paused')
      return 'failed'
    }
  }, [
    openLocalPipeline,
    resetInternal,
    setError,
    setState,
    switchToFallback,
    teardown,
  ])

  // sessionStorage の paused エントリを engine に復元する (= 患者切替 / reload を跨いだ
  // 一時停止の再表示. useRecordingStatus が表示中 event と一致するエントリを見つけて呼ぶ).
  const adoptPausedSession = useCallback(
    (info: PausedSessionInfo) => {
      if (stateRef.current !== 'idle') return
      // 終端済み session のゾンビ復元を拒否 (完了/破棄直後の stale event キャッシュ対策.
      // サーバ側の paused_streaming_session は次の poll 再取得で null に収束する).
      if (finishedSessionIdsRef.current.has(info.sessionId)) return
      console.info(
        '[transcription-dispatch] useRecordingEngine.adoptPausedSession',
        { session_id: info.sessionId }
      )
      // paused session は構造上 streaming のみ (pause は streaming 'recording' 中限定).
      // 直前の batch 録音から bit が残っている (reset は生成中バナー用に消さない) と、
      // adopt 後の劣化 fallback で黄バナーが抑止される (= 劣化の隠蔽) ためここで消す.
      setIsIntendedBatch(false)
      setSessionId(info.sessionId)
      sessionIdRef.current = info.sessionId
      setRecordingEventIds(info.eventIds)
      segmentIndexRef.current = info.nextSegmentIndex
      setStagedDurationS(info.stagedDurationS)
      setState('paused')
    },
    [setIsIntendedBatch, setRecordingEventIds, setState]
  )

  const cancel = useCallback(async (): Promise<CancelResult> => {
    // backend に cancel を通知して runner を graceful close + SS=CANCELLED("user_cancelled") に倒す.
    // 通知できないと Chirp stream が 30 秒タイムアウトを待つことになり、ユーザーが
    // 再録音しようとしても 旧 SS が残って弾かれる. 通知失敗は best effort で無視
    // (backend 側に start 時の CANCELLED/FAILED SS 自動 delete fallback もある).
    //
    // 注意: ACTIVE 以外 (FINALIZED 等) への cancel は backend 側で no-op で受け付ける.
    // ただし完了後の cleanup 用途では reset() を使うこと (重複 backend POST を避けるため).
    //
    // start() 進行中に cancel() された場合: startGenRef を ++ して in-flight start を
    // invalidate する. start() は次の await 後に myGen !== startGenRef.current を
    // 検知し、(まだ握っていれば) 作成済 SS を backend cancel しつつ静かに撤退する.
    // sessionIdRef.current にはまだ session_id が入っていない可能性があるので、
    // ここの cancelStream(sid) だけでは backend 通知が漏れる場合があるが、その
    // 取りこぼしは start() 側の invalidated 経路でカバーされる.
    startGenRef.current++

    // 意図的キャンセル = batch 生成は走らないので低速 notice flag も消す
    // (resetInternal は完了後 cleanup と共用のため意図的に消さない. ここで明示クリア).
    // isIntendedBatch も同じライフサイクル: 低速 notice の文言分岐に使われるため
    // reset (完了後 cleanup) では残し、 start / cancel でだけ消す.
    setIsSlowTranscription(false)
    setIsIntendedBatch(false)

    const sid = sessionIdRef.current
    // mic の解放と UI の idle 復帰は同期で先に済ませる (待たせない).
    resetInternal()

    if (!sid) return { deletedEventIds: [] }
    console.info(
      '[transcription-dispatch] useRecordingEngine.cancel: notifying backend',
      { session_id: sid }
    )
    finishedSessionIdsRef.current.add(sid)
    // backend の応答まで await できるよう promise を返す (順序の意味は呼び出し元が持つ).
    const res = await cancelStream(sid).catch(() => {
      // 410 / 409 / network いずれも UI 上は cancel 完了に倒す (従来方針).
      return { deleted_event_ids: [] }
    })
    return { deletedEventIds: res.deleted_event_ids }
  }, [resetInternal, setIsIntendedBatch])

  const reset = useCallback(() => {
    // 完了後 cleanup. backend には通知せず内部 state のみ idle に戻す.
    // caller hook が `state === 'completed'` / `'failed'` 後に重複 backend POST を避けつつ
    // 次の録音セッションに備えるための後始末として呼ぶ.
    console.info(
      '[transcription-dispatch] useRecordingEngine.reset: local cleanup only'
    )
    resetInternal()
  }, [resetInternal])

  // RecordingFailedAlert (= MeetingBlock 配線) の救済 action.
  // pendingBlob を保持中の `state==='failed'` から再投入する.
  // - fallback 経路で commit 失敗 → fallbackCommitRef を再呼出 (= legacy batch 再 POST)
  // - streaming 経路で finalize 失敗 → finalizeStream を再呼出 (= multipart 再投入)
  // 成功で `state='completed'` + pendingBlob クリア、 失敗で `state='failed'` のまま.
  const retry = useCallback(async () => {
    const blob = pendingBlobRef.current
    if (!blob) {
      console.warn(
        '[transcription-dispatch] useRecordingEngine.retry: no pending blob, skip'
      )
      return
    }
    const wasInFallback = isFallbackRef.current
    const sid = sessionIdRef.current

    console.info(
      '[transcription-dispatch] useRecordingEngine.retry: re-submitting pending blob',
      { path: wasInFallback ? 'fallback' : 'streaming', blob_size: blob.size }
    )
    setError(null)
    setState(
      pendingSubmitModeRef.current === 'pause' ? 'pausing' : 'finalizing'
    )
    try {
      if (pendingSubmitModeRef.current === 'pause') {
        // pause POST 失敗からの再送 (log/185). back は同 segment_index を upsert するため冪等.
        if (!sid) {
          throw new Error('session_id not set; cannot retry pause')
        }
        const res = await pauseStreamingSession(
          sid,
          blob,
          segmentIndexRef.current
        )
        segmentIndexRef.current = res.next_segment_index
        setStagedDurationS(res.staged_duration_s)
        pendingSubmitModeRef.current = 'finalize'
        pendingBlobRef.current = null
        setPendingBlob(null)
        setState('paused')
        return
      }
      if (wasInFallback) {
        if (sid && segmentIndexRef.current > 0) {
          // salvage finalize の再送 (log/185. stop() fallback 経路と同じ routing).
          const res = await finalizeStream(
            sid,
            blob,
            captureSettingsRef.current
          )
          if (res.transcription_mode === 'batch_fallback') {
            setIsSlowTranscription(true)
          }
        } else {
          await fallbackCommitRef.current(blob)
          // client 起点 fallback の commit 成功 = legacy batch (低速) での文字起こし確定.
          setIsSlowTranscription(true)
        }
      } else {
        if (!sid) {
          throw new Error('session_id not set; cannot retry streaming finalize')
        }
        const res = await finalizeStream(sid, blob, captureSettingsRef.current)
        if (res.transcription_mode === 'batch_fallback') {
          setIsSlowTranscription(true)
        }
      }
      pendingBlobRef.current = null
      setPendingBlob(null)
      if (sid) {
        finishedSessionIdsRef.current.add(sid)
      }
      setState('completed')
    } catch (err) {
      // streaming 同 session_id への再 finalize は back 側で runner が pop 済 (= 1 度目で必ず pop)
      // のため 409 = StreamingSessionNotFoundError を返す. retry が機能する条件は実質
      // 「1 度目が back に到達しなかった network error」 のみ. 409/4xx 系は
      // toRecordingSubmitFailedError が isRetryHopeless=true の DL 誘導文言に変換し、
      // RecordingFailedAlert が再試行ボタンを隠して [音声をダウンロード] 導線を示す.
      console.error(
        '[transcription-dispatch] useRecordingEngine.retry: re-submit failed',
        err
      )
      setError(toRecordingSubmitFailedError(err))
      setState('failed')
    }
  }, [setState, setError])

  // RecordingFailedAlert の救済 action 2: 保持 blob を local file として DL する.
  // ファイル名 = `kakeru-recording-{ISO 日時}.{ext}`. Blob.type から拡張子を推定 (mp4/webm).
  const downloadBlob = useCallback(() => {
    const blob = pendingBlobRef.current
    if (!blob) {
      console.warn(
        '[transcription-dispatch] useRecordingEngine.downloadBlob: no pending blob, skip'
      )
      return
    }
    const mime = blob.type || 'audio/mp4'
    // mime から短い拡張子を推定. 不明なら mp4 を fallback とする.
    const ext = mime.includes('webm')
      ? 'webm'
      : mime.includes('mp4')
      ? 'm4a'
      : 'mp4'
    const ts = new Date().toISOString().replace(/[:.]/g, '-')
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `kakeru-recording-${ts}.${ext}`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }, [])

  return {
    state,
    sessionId,
    recordingSessionId,
    recordingEventIds,
    error,
    fallbackBlob,
    pendingBlob,
    hasPendingBlob: pendingBlob !== null,
    isOffline,
    isSlowTranscription,
    isIntendedBatch,
    stream,
    start,
    stop,
    pause,
    resume,
    adoptPausedSession,
    stagedDurationS,
    cancel,
    reset,
    retry,
    downloadBlob,
  }
}
